import { usePlatformLocationFeatureV1QueryUpdaters } from '../query-updaters';
import { CreateOrUpdateLocationNotificationPreferenceIO } from '../types';
import {
  UsePlatformLocationFeatureV1MutationEndpointArgs,
  usePlatformLocationFeatureV1Mutation,
} from './use-platform-location-feature-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `CreateOrUpdateLocationNotificationPreference` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useCreateOrUpdateLocationNotificationPreferenceMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends CreateOrUpdateLocationNotificationPreferenceIO['input'] = CreateOrUpdateLocationNotificationPreferenceIO['input']
>({
  options,
  // optimisticUpdate = false, // uncomment if needed
  ...args
}: UsePlatformLocationFeatureV1MutationEndpointArgs<
  'CreateOrUpdateLocationNotificationPreference',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const { invalidateQueries } = usePlatformLocationFeatureV1QueryUpdaters();
  return usePlatformLocationFeatureV1Mutation<
    'CreateOrUpdateLocationNotificationPreference',
    E,
    MutationContext<C>,
    OtherOptions,
    RequestOverride
  >({
    endpointName: 'CreateOrUpdateLocationNotificationPreference',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here
        invalidateQueries({
          endpointName: 'CheckLocationNotificationPreference',
          queryFilters: {
            predicate: ({ queryKey }) => {
              const cachedRequest = queryKey[2];
              return (
                cachedRequest.locationId === request.locationId &&
                cachedRequest.notificationEnum === request.notificationEnum &&
                !!request.sourceEnum?.some((sourceEnum) => sourceEnum === cachedRequest.sourceEnum)
              );
            },
            exact: false,
          },
          // TODO: Milind add filters for notificationFeature, source and locationId
        });
        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
